/**
 * The top bar: a search box and a feed of what needs you.
 *
 * Both live here rather than on a screen of their own because both are answers
 * to questions people have *while doing something else* — "where is that bill"
 * and "is anything waiting for me". A page somebody has to navigate to in order
 * to find out whether they need to navigate somewhere is not one they visit.
 *
 * Neither widens anything. Search asks the server, which runs a provider only
 * if the caller may see that kind of document and narrows every query through
 * the scope layer; the feed reads the audit log filtered to what concerns the
 * reader. The frontend does not decide any of that, and cannot.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'

interface Hit {
  kind: string
  label: string
  reference: string
  route: string
  detail: string
  amount_paise: number | null
  on: string | null
  status: string | null
  id: string
}

interface SearchResults {
  query: string
  hits: Hit[]
  not_permitted: string[]
  too_short: boolean
}

interface FeedEvent {
  seq: number
  at: string
  action: string
  object_type: string
  object_id: string | null
  actor_name: string
  says: string
  reason: string | null
  unread: boolean
}

interface Waiting {
  kind: string
  label: string
  count: number
  route: string
  detail: string
  urgent: boolean
}

interface Feed {
  events: FeedEvent[]
  waiting: Waiting[]
  unread: number
  high_water: number
}

/** How long to wait after the last keystroke. Long enough not to search on
 *  every letter, short enough that it does not feel broken. */
const DEBOUNCE_MS = 250

function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}

function when(iso: string): string {
  const at = new Date(iso)
  const minutes = Math.round((Date.now() - at.getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`
  return at.toISOString().slice(0, 10)
}

function SearchBox() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const query = useDebounced(text, DEBOUNCE_MS)
  const box = useRef<HTMLDivElement>(null)

  // Clicking anywhere else closes it. Without this the results sit over the
  // page until the next keystroke, which reads as the page being stuck.
  useEffect(() => {
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])

  const results = useQuery({
    queryKey: ['search', query],
    queryFn: () => api.get<SearchResults>(`/search?q=${encodeURIComponent(query)}`),
    enabled: can('search.document.view_own') && query.trim().length >= 2,
  })

  if (!can('search.document.view_own')) return null
  const data = results.data

  return (
    <div className="searchbox" ref={box}>
      <input
        type="search"
        value={text}
        placeholder="Find a reference, a name, or an amount"
        onChange={(e) => {
          setText(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
        }}
      />
      {open && query.trim().length >= 2 && (
        <div className="results">
          {results.isFetching && <div className="empty">Looking…</div>}
          {data && data.hits.length === 0 && !results.isFetching && (
            <div className="empty">Nothing matching “{data.query}”.</div>
          )}
          {(data?.hits ?? []).map((hit) => (
            <button
              key={`${hit.kind}-${hit.id}`}
              className="hit"
              onClick={() => {
                navigate(hit.route)
                setOpen(false)
                setText('')
              }}
            >
              <span className="mono">{hit.reference}</span>
              <span className="faint">{hit.label}</span>
              <span>{hit.detail}</span>
              <span className="num">
                {hit.amount_paise === null ? '' : formatInr(hit.amount_paise)}
              </span>
            </button>
          ))}
          {(data?.not_permitted ?? []).length > 0 && (
            <div className="faint" style={{ padding: '8px 12px' }}>
              Not searched, because your role cannot see them:{' '}
              {(data?.not_permitted ?? []).join(', ')}.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function FeedButton() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const panel = useRef<HTMLDivElement>(null)

  const feed = useQuery({
    queryKey: ['feed'],
    queryFn: () => api.get<Feed>('/workspace/feed'),
    enabled: can('inbox.item.view_own'),
    // Polled rather than pushed. A websocket for four people checking whether
    // a bill was approved is infrastructure bought for nothing; a minute is
    // well inside how fast anybody needs to know.
    refetchInterval: 60_000,
  })

  const read = useMutation({
    mutationFn: (upto: number) =>
      api.post<Feed>('/workspace/feed/read', { upto_seq: upto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['feed'] }),
  })

  useEffect(() => {
    const away = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])

  if (!can('inbox.item.view_own')) return null
  const data = feed.data
  const waiting = data?.waiting ?? []
  const urgent = waiting.some((w) => w.urgent)

  return (
    <div className="feed" ref={panel}>
      <button
        className={`bell ${urgent ? 'urgent' : ''}`}
        onClick={() => setOpen(!open)}
        title="What needs you"
      >
        {waiting.reduce((sum, w) => sum + w.count, 0) || '—'}
        {(data?.unread ?? 0) > 0 && <span className="dot" />}
      </button>

      {open && (
        <div className="panel">
          <div className="panel-head">
            <strong>Waiting for you</strong>
          </div>
          {waiting.length === 0 ? (
            <div className="empty">Nothing waiting.</div>
          ) : (
            waiting.map((item) => (
              <button
                key={item.kind}
                className={`waiting ${item.urgent ? 'urgent' : ''}`}
                onClick={() => {
                  navigate(item.route)
                  setOpen(false)
                }}
              >
                <span>
                  <strong>{item.count}</strong> {item.label}
                </span>
                <span className="faint">{item.detail}</span>
              </button>
            ))
          )}

          <div className="panel-head">
            <strong>What happened</strong>
            {(data?.unread ?? 0) > 0 && (
              <button
                className="small"
                onClick={() => read.mutate(data?.high_water ?? 0)}
              >
                Mark read
              </button>
            )}
          </div>
          {(data?.events ?? []).length === 0 ? (
            <div className="empty">
              Nothing since you last looked. You are never told about your own
              actions.
            </div>
          ) : (
            (data?.events ?? []).map((event) => (
              <div
                key={event.seq}
                className={`event ${event.unread ? 'unread' : ''}`}
              >
                <span>{event.says}</span>
                {event.reason && <span className="faint">“{event.reason}”</span>}
                <span className="faint">{when(event.at)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export function Workspace() {
  return (
    <div className="workspace">
      <SearchBox />
      <FeedButton />
    </div>
  )
}
